// MeetTutors.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from './firebase';
import api from './utils/api';
import './MeetTutors.css';

/*
 * Meet the Tutors Component
 * 
 * This component displays public tutor profiles in a clean, responsive grid layout.
 * 
 * Data Management:
 * - Fetches tutor data from /api/getTutorsPublic endpoint
 * - Displays: firstName, lastName, subjects, bio, profileImage (optional)
 * - Falls back to sample data when API is unavailable
 * 
 * To update tutor profiles after deployment:
 * 1. Tutors can update their profiles through the Dashboard
 * 2. Add 'bio' and 'profileImage' fields to the user collection in Firebase
 * 3. The data will automatically appear on this page
 * 
 * Future enhancements:
 * - Real profile images (currently shows initials)
 * - Individual tutor detail pages
 * - Filtering by subject or availability
 */

const MeetTutors = () => {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadTutors = async () => {
      try {
        // Try to fetch from the API first
        const response = await api.get('/api/getTutorsPublic');
        const data = await response.json();
        
        if (response.ok && data.tutors && data.tutors.length > 0) {
          setTutors(data.tutors);
        } else {
          // Fallback to sample data when API is not available or returns no tutors
          setTutors([
            {
              id: '1',
              firstName: 'Riley',
              lastName: 'Smith',
              subjects: ['math', 'physics', 'engineering'],
              bio: 'Engineering student passionate about mathematics and physics. Loves helping students understand complex concepts through simple explanations.'
            },
            {
              id: '2',
              firstName: 'Declan',
              lastName: 'Johnson',
              subjects: ['chemistry', 'biology', 'science'],
              bio: 'Dedicated science tutor with a focus on chemistry and biology. Believes in hands-on learning and making science fun and accessible.'
            },
            {
              id: '3',
              firstName: 'Emma',
              lastName: 'Wilson',
              subjects: ['english', 'french', 'writing'],
              bio: 'Language enthusiast and creative writing tutor. Helps students develop strong communication skills and love for literature.'
            }
          ]);
        }
      } catch (err) {
        console.error('Error fetching tutors:', err);
        // Use sample data when API is not available
        setTutors([
          {
            id: '1',
            firstName: 'Riley',
            lastName: 'Smith',
            subjects: ['math', 'physics', 'engineering'],
            bio: 'Engineering student passionate about mathematics and physics. Loves helping students understand complex concepts through simple explanations.'
          },
          {
            id: '2',
            firstName: 'Declan',
            lastName: 'Johnson',
            subjects: ['chemistry', 'biology', 'science'],
            bio: 'Dedicated science tutor with a focus on chemistry and biology. Believes in hands-on learning and making science fun and accessible.'
          },
          {
            id: '3',
            firstName: 'Emma',
            lastName: 'Wilson',
            subjects: ['english', 'french', 'writing'],
            bio: 'Language enthusiast and creative writing tutor. Helps students develop strong communication skills and love for literature.'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadTutors();
  }, []);

  const getInitials = (firstName, lastName, name) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return name.charAt(0).toUpperCase();
    }
    return 'T';
  };

  const getFullName = (tutor) => {
    if (tutor.firstName && tutor.lastName) {
      return `${tutor.firstName} ${tutor.lastName}`;
    }
    return tutor.name || 'Tutor';
  };

  if (loading) {
    return (
      <div className="meet-tutors-wrapper">
        <div className="meet-tutors-container">
          <div className="loading-message">Loading our amazing tutors...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="meet-tutors-wrapper">
      <div className="meet-tutors-container">
        <div className="meet-tutors-header">
          <h1>Meet Our Tutors</h1>
          <p>
            Get to know the talented university students who make BrightBridge special. 
            Our tutors are passionate, knowledgeable, and dedicated to helping your child succeed.
          </p>
        </div>

        {tutors.length === 0 ? (
          <div className="no-tutors-message">
            Our tutors will be featured here soon! Check back later to meet our amazing team.
          </div>
        ) : (
          <div className="tutors-grid">
            {tutors.map((tutor) => {
              const tutorName = getFullName(tutor);
              const handleTutorClick = () => {
                // Check if user is logged in
                const user = auth.currentUser;
                if (user) {
                  // Navigate to dashboard with tutor info in state
                  navigate('/Dashboard', { 
                    state: { 
                      selectedTutor: {
                        name: tutorName,
                        email: tutor.email,
                        firstName: tutor.firstName,
                        lastName: tutor.lastName,
                        subjects: tutor.subjects
                      }
                    }
                  });
                } else {
                  // Not logged in, redirect to auth page
                  navigate('/Auth', {
                    state: {
                      redirectTo: '/Dashboard',
                      selectedTutor: {
                        name: tutorName,
                        email: tutor.email,
                        firstName: tutor.firstName,
                        lastName: tutor.lastName,
                        subjects: tutor.subjects
                      }
                    }
                  });
                }
              };

              return (
                <div key={tutor.id} className="tutor-card">
                  <button 
                    className="tutor-card-hover-button"
                    onClick={handleTutorClick}
                  >
                    Book With {tutorName}
                  </button>
                  <div className="tutor-card-content">
                    <div className="tutor-image">
                      {tutor.profileImage ? (
                        <img src={tutor.profileImage} alt={tutorName} />
                      ) : (
                        getInitials(tutor.firstName, tutor.lastName, tutor.name)
                      )}
                    </div>
                    
                    <div className="tutor-name">
                      {tutorName}
                    </div>
                    
                    {tutor.subjects && tutor.subjects.length > 0 && (
                      <div className="tutor-subjects">
                        {tutor.subjects.map((subject, index) => (
                          <span key={index} className="subject-tag">
                            {subject.charAt(0).toUpperCase() + subject.slice(1)}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {tutor.bio && (
                      <div className="tutor-bio">
                        {tutor.bio}
                      </div>
                    )}
                    
                    {!tutor.bio && (
                      <div className="tutor-bio">
                        A dedicated tutor passionate about helping students achieve their academic goals.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetTutors;
